import { ArrowBack } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  styled,
  SvgIconTypeMap,
  Theme,
  Typography,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import ErrorBoundary from 'components/ErrorBoundary';
import ActionButtons, {
  type ActionButtonType,
} from 'further-ui/components/ActionButtons';
import { Column, Row } from 'further-ui/components/Layout';
import React, { PropsWithChildren } from 'react';
import { useHistory } from 'react-router-dom';
import PageActionBar from './PageActionBar';
import PageMessages, { MessageType } from './PageMessages';
import { Helmet } from 'react-helmet';

const PageWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  height: `calc(100vh - ${theme.searchBar.height + 2}px)`,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
}));

const Centered = styled(Box)({
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const Scrollable = styled(Box)({
  width: '100%',
  height: '100%',
  overflowY: 'auto',
});

const Content = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'disableTopPadding',
})<{
  disableTopPadding?: boolean;
}>(({ theme, disableTopPadding }) => ({
  width: '100%',
  minHeight: '100%',
  display: 'flex',
  padding: theme.spacing(theme.spacingSizes.xl),

  ...(disableTopPadding && {
    paddingTop: 0,
  }),
}));

const Wrapper = styled(Column)({
  width: '100%',
  alignItems: 'stretch',
});

const GoBackButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xxs),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,

  '&:hover': {
    backgroundColor: 'transparent',
    borderColor: theme.palette.grey[400],
  },
}));

const HeadingIconWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xxs),
  border: `1px solid ${theme.palette.grey[300]}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.shape.borderRadius,
}));

const HeaderContainer = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'sticky',
})<{
  sticky?: boolean;
}>(({ theme, sticky }) => ({
  backgroundColor: theme.palette.background.paper,

  ...(sticky && {
    paddingTop: theme.spacing(theme.spacingSizes.xl),
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,
  }),
}));

const Header = styled(Row)({
  minHeight: 32,
});

type Props = {
  heading?: string | React.ReactNode;
  subheading?: string | React.ReactNode;
  headingIcon?: OverridableComponent<SvgIconTypeMap<object>>;
  messages?: Array<MessageType>;
  tabTitle?: string;
  actions?: Array<ActionButtonType>;
  goBackAction?: string | (() => void);
  spacing?: keyof Theme['spacingSizes'];
  customActions?: React.ReactNode;
  actionBar?: React.ReactNode;
  withHeadingDivider?: boolean;
  withStickyHeader?: boolean;
};

const Page: React.FC<PropsWithChildren<Props>> = ({
  tabTitle,
  heading,
  subheading,
  headingIcon: HeadingIcon,
  children,
  messages,
  actions,
  spacing,
  customActions,
  actionBar,
  goBackAction,
  withHeadingDivider,
  withStickyHeader,
}) => {
  const history = useHistory();
  const showHeaderRow =
    !!heading || !!actions?.length || !!goBackAction || !!HeadingIcon;

  const handleGoBack = () => {
    if (typeof goBackAction === 'string') {
      history.push(goBackAction);
    } else {
      goBackAction?.();
    }
  };

  return (
    <PageWrapper>
      {tabTitle ? (
        <Helmet>
          <title>{tabTitle}</title>
        </Helmet>
      ) : null}
      {!!messages?.length && <PageMessages messages={messages} />}
      <Scrollable>
        <ErrorBoundary
          loader={
            <Centered>
              <CircularProgress size={24} />
            </Centered>
          }
        >
          <Content disableTopPadding={withStickyHeader}>
            <Wrapper spacing={spacing}>
              {showHeaderRow ? (
                <HeaderContainer sticky={withStickyHeader} spacing="lg">
                  <Header centered spacing="sm" justify="space-between">
                    {!!heading && (
                      <Row centered spacing="sm">
                        {!!goBackAction && (
                          <GoBackButton onClick={handleGoBack}>
                            <ArrowBack fontSize="small" />
                          </GoBackButton>
                        )}
                        <Row centered spacing="sm">
                          {!!HeadingIcon && (
                            <HeadingIconWrapper>
                              <HeadingIcon fontSize="small" />
                            </HeadingIconWrapper>
                          )}
                          <Column>
                            <Typography variant="h4">{heading}</Typography>
                            {subheading ? (
                              <Typography variant="body2" color="grey.600">
                                {subheading}
                              </Typography>
                            ) : null}
                          </Column>
                        </Row>
                      </Row>
                    )}
                    {customActions ? (
                      <Row centered spacing="sm" justify="flex-end">
                        {customActions}
                      </Row>
                    ) : (
                      <ActionButtons actions={actions} />
                    )}
                  </Header>
                  {withHeadingDivider ? <Divider /> : null}
                </HeaderContainer>
              ) : null}
              {children}
            </Wrapper>
          </Content>
        </ErrorBoundary>
      </Scrollable>
      {actionBar ? <PageActionBar>{actionBar}</PageActionBar> : null}
    </PageWrapper>
  );
};

export default Page;
